import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import BlogCard from '../components/BlogCard';
import ContentRenderer from '../components/ContentRenderer';
import Footer from '../../components/Footer';
import logo from '../../assets/wowlogo.jpeg';
import {
  getBlogBySlug,
  getPublishedBlogs,
  incrementViews,
  getClientSettings,
} from '../../firebase/blogService';
import { formatDate } from '../../shared/utils';
import Loading from '../../shared/Loading';
import '../user.css';

const CLIENT_ID = process.env.REACT_APP_CLIENT_ID;

export default function BlogDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!CLIENT_ID) { setLoading(false); return; }
    window.scrollTo(0, 0);
    setLoading(true);
    setNotFound(false);

    (async () => {
      const [p, s, allPosts] = await Promise.all([
        getBlogBySlug(CLIENT_ID, slug),
        getClientSettings(CLIENT_ID),
        getPublishedBlogs(CLIENT_ID, 10),
      ]);

      if (!p) { setNotFound(true); setLoading(false); return; }

      setPost(p);
      setSettings(s);

      // Apply primary color
      if (s?.primaryColor) {
        document.documentElement.style.setProperty('--client-primary', s.primaryColor);
      }

      // Related: same category, exclude current
      const rel = allPosts
        .filter((b) => b.id !== p.id && (!p.category || b.category === p.category))
        .slice(0, 3);
      setRelated(rel.length ? rel : allPosts.filter((b) => b.id !== p.id).slice(0, 3));

      // Increment view count (fire-and-forget)
      incrementViews(CLIENT_ID, p.id).catch(() => {});
      setLoading(false);
    })();
  }, [slug]);

  if (loading) return <Loading fullScreen color="var(--u-accent, #3b82f6)" />;

  if (notFound) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', fontFamily: 'Inter, sans-serif' }}>
        <header className="u-detail-topbar">
          <div className="u-container u-detail-topbar-inner">
            <Link to="/blog" className="u-detail-brand">
              <img src={logo} alt="Wacation On Wheels" className="u-brand-image" />
              <div>
                <div className="u-navbar-name">{settings?.blogName || 'WOW Caravans'}</div>
                {settings?.tagline && <div className="u-navbar-tagline">{settings.tagline}</div>}
              </div>
            </Link>
          </div>
        </header>
        <main style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="u-empty">
            <div className="u-empty-icon">🔍</div>
            <div className="u-empty-title">Article Not Found</div>
            <div className="u-empty-text">
              The article you're looking for doesn't exist or has been removed.
            </div>
            <Link to="/blog"
              style={{ display: 'inline-flex', marginTop: 20, alignItems: 'center', gap: 6, color: 'var(--u-accent, #3b82f6)', fontWeight: 700, fontSize: 14 }}>
              ← Back to all articles
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const initials = (post.authorName || 'A')[0].toUpperCase();

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', fontFamily: 'Inter, sans-serif' }}>
      <header className="u-detail-topbar">
        <div className="u-container u-detail-topbar-inner">
          <Link to="/blog" className="u-detail-brand">
            <img src={logo} alt="Wacation On Wheels" className="u-brand-image" />
            <div>
              <div className="u-navbar-name">{settings?.blogName || 'WOW Caravans'}</div>
              {settings?.tagline && <div className="u-navbar-tagline">{settings.tagline}</div>}
            </div>
          </Link>

          <Link to="/blog" className="u-detail-topbar-link">
            Back to Blogs
          </Link>
        </div>
      </header>

      <main style={{ flex: 1 }}>
        {/* Hero */}
        {post.coverImage ? (
          <div className="u-detail-hero">
            <img src={post.coverImage} alt={post.title} className="u-detail-hero-img" />
            <div className="u-detail-hero-overlay" />
          </div>
        ) : (
          <div style={{ height: 8, background: 'var(--u-accent, #3b82f6)' }} />
        )}

        {/* Article content */}
        <div className="u-detail-wrapper">
          {/* Back button */}
          <button
            className="u-back-btn"
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/blog'))}
            style={{ marginBottom: 24 }}
          >
            ← Back
          </button>

          <div className="u-detail-header">
            {/* Category */}
            <div className="u-detail-categories">
              {post.category && (
                <span className="u-detail-category">{post.category}</span>
              )}
              {post.featured && (
                <span className="u-detail-category" style={{ background: 'var(--u-accent-light, #eff6ff)', color: 'var(--u-accent, #3b82f6)' }}>
                  ⭐ Featured
                </span>
              )}
            </div>

            {/* Title */}
            <h1 className="u-detail-title">{post.title}</h1>

            {/* Excerpt */}
            {post.excerpt && (
              <p className="u-detail-excerpt">{post.excerpt}</p>
            )}

            {/* Author & Meta */}
            <div className="u-detail-meta">
              <div className="u-detail-author-avatar">{initials}</div>
              <div>
                <div className="u-detail-author-name">{post.authorName || 'Author'}</div>
                {post.authorBio && (
                  <div className="u-detail-author-bio">{post.authorBio}</div>
                )}
              </div>

              {post.publishedAt && (
                <>
                  <div className="u-detail-meta-divider" />
                  <div className="u-detail-meta-item">
                    <span className="u-detail-meta-label">Published</span>
                    <span className="u-detail-meta-value">{formatDate(post.publishedAt)}</span>
                  </div>
                </>
              )}

              {post.readTime && (
                <>
                  <div className="u-detail-meta-divider" />
                  <div className="u-detail-meta-item">
                    <span className="u-detail-meta-label">Read Time</span>
                    <span className="u-detail-meta-value">{post.readTime} min</span>
                  </div>
                </>
              )}

              {post.views !== undefined && (
                <>
                  <div className="u-detail-meta-divider" />
                  <div className="u-detail-meta-item">
                    <span className="u-detail-meta-label">Views</span>
                    <span className="u-detail-meta-value">{(post.views + 1).toLocaleString()}</span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Content */}
          <ContentRenderer content={post.content} />

          {/* Tags */}
          {post.tags?.length > 0 && (
            <div className="u-tags">
              {post.tags.map((tag) => (
                <Link key={tag} to={`/blog?search=${encodeURIComponent(tag)}`} className="u-tag">
                  #{tag}
                </Link>
              ))}
            </div>
          )}

          {/* Author card */}
          {post.authorName && (
            <div style={{
              marginTop: 48, padding: '28px 32px',
              background: 'var(--u-bg-soft, #f8fafc)',
              border: '1px solid var(--u-border, #e2e8f0)',
              borderRadius: 16, display: 'flex', gap: 20, alignItems: 'flex-start',
            }}>
              <div style={{
                width: 60, height: 60, borderRadius: '50%', flexShrink: 0,
                background: 'linear-gradient(135deg, var(--u-accent, #3b82f6), #818cf8)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontSize: 24, fontWeight: 800,
              }}>
                {initials}
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.08em', fontWeight: 700, marginBottom: 4 }}>
                  Written by
                </div>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#0f172a', marginBottom: 6 }}>
                  {post.authorName}
                </div>
                {post.authorBio && (
                  <p style={{ fontSize: 14, color: '#64748b', lineHeight: 1.7 }}>{post.authorBio}</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Related posts */}
        {related.length > 0 && (
          <div className="u-related">
            <div className="u-container">
              <div className="u-section-header">
                <h2 className="u-section-title"><span />More Articles</h2>
              </div>
              <div className="u-blog-grid">
                {related.map((p) => <BlogCard key={p.id} post={p} />)}
              </div>
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
